import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { api } from './api.service';
import { Calendar, TypeOfAppointment } from '../features/calendar/calendar-slice';
import { converToLocalTimeZone, isAfternoon, isEvening, isMorning } from '../lib/dayjs';
import { EnumJourneyAction, getJourneyAction } from '../utils/enum/journeyAction';
import { IJourneyResp, ISchedules } from './journey.service';
import { getCurrentSchedule } from '../components/shared/JourneyActionManager';
import dayjs from 'dayjs';

interface IAvailableTimesRequest {
  calendarsAvailables: Calendar[];
  month: string;
  year: string;
  journey: IJourneyResp;
  doctorId: string | null;
  withTheSameDoctor: boolean | null;
  typeOfAppointment: TypeOfAppointment | null;
  isReschedule?: boolean | null;
  schedule?: ISchedules | null;
}
export interface IAppointments {
  availableList: string[];
  availableListAddress: { [key: string]: string[] }[];
}

export interface FeaturedScheduleDay {
  date: string;
  times: string[];
}

const DAY_FORMAT = 'YYYY-MM-DD';

export const getFeaturedScheduleDays = (
  availableListAddress: { [key: string]: string[] }[],
  maxDays = 3,
  maxSlotsPerDay = 3
): FeaturedScheduleDay[] => {
  if (!Array.isArray(availableListAddress) || maxDays <= 0 || maxSlotsPerDay <= 0) return [];
  const now = dayjs();
  const endExclusive = now.startOf('day').add(7, 'day');
  const nowTs = now.valueOf();
  const endTs = endExclusive.valueOf();

  const timesByDay = new Map<string, Set<string>>();

  for (const item of availableListAddress) {
    for (const times of Object.values(item ?? {})) {
      if (!Array.isArray(times)) continue;

      for (const time of times) {
        const parsed = dayjs(time);
        if (!parsed.isValid()) continue;
        const ts = parsed.valueOf();
        if (ts < nowTs || ts >= endTs) continue;

        const day = parsed.format(DAY_FORMAT);
        if (!timesByDay.has(day)) timesByDay.set(day, new Set());
        timesByDay.get(day)?.add(time);
      }
    }
  }

  const sortedDays = Array.from(timesByDay.keys()).sort();
  const shiftCheckers = [isMorning, isAfternoon, isEvening];

  return sortedDays.slice(0, maxDays).map(date => {
    const dayTimes = Array.from(timesByDay.get(date) ?? []).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

    const used = new Set<string>();
    const times: string[] = [];

    for (const isInShift of shiftCheckers) {
      if (times.length >= maxSlotsPerDay) break;

      const chosen = dayTimes.find(time => !used.has(time) && isInShift(time)) ?? dayTimes.find(time => !used.has(time));
      if (!chosen) break;

      used.add(chosen);
      times.push(chosen);
    }

    times.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

    return { date, times };
  });
};

export const availableTimes = api.injectEndpoints({
  endpoints: build => ({
    getAvailableTimes: build.mutation<IAppointments, IAvailableTimesRequest>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const {
          calendarsAvailables,
          journey,
          month,
          year,
          doctorId,
          withTheSameDoctor,
          typeOfAppointment,
          isReschedule,
          schedule
        } = _arg;
        const { calendarSecundary } = journey;
        const status = getJourneyAction(journey);
        const currentSchedule = schedule ? schedule : (getCurrentSchedule(journey) as ISchedules);
        const isReturnPhysicalAppointment =
          status === EnumJourneyAction.AWAITING_SCHEDULE_RETURN || currentSchedule.returnSchedule === true;
        const calendarsToFecth = isReturnPhysicalAppointment
          ? calendarsAvailables.filter(calendar => calendar.type === typeOfAppointment)
          : calendarsAvailables;

        const buildQuery = (calendar: Calendar): string => {
          if (isReschedule)
            return `calendars/${calendar.id}/availableTimes/reschedule/${currentSchedule.id}/${month}/${year}/${
              currentSchedule.returnSchedule
            }${withTheSameDoctor ? `/${doctorId}` : ''}`;
          if (status === EnumJourneyAction.AWAITING_SCHEDULE_RETURN)
            return `calendars/${calendar.id}/${doctorId}/${journey.journeyid}/returnAvailableTimes/${month}/${year}`;
          if (
            (status === EnumJourneyAction.VIDEOCALL_AVAILABLE && withTheSameDoctor) ||
            (status === EnumJourneyAction.AWAITING_CONSULTATION && withTheSameDoctor) ||
            (calendarSecundary && withTheSameDoctor)
          )
            return `calendars/${calendar.id}/${doctorId}/availableTimes/${month}/${year}/false`;
          return `calendars/${calendar.id}/availableTimes/${month}/${year}`;
        };

        const fetchCalendar = async (calendar: Calendar) => {
          const response = await fetchWithBQ(buildQuery(calendar));
          if (response.error) return { error: response.error as FetchBaseQueryError };
          return { data: response.data as string[] };
        };

        const fetchAllCalendars = async (calendar: Calendar): Promise<IAppointments> => {
          const response = await fetchCalendar(calendar);
          if (response.error) return { availableList: [], availableListAddress: [] };
          const requestResponse: IAppointments = { availableList: [], availableListAddress: [] };
          const availableTimeConvertedToLocalTimeZone = converToLocalTimeZone(response.data as string[]);
          requestResponse.availableList.push(...availableTimeConvertedToLocalTimeZone);
          requestResponse.availableListAddress?.push({ [calendar.id]: availableTimeConvertedToLocalTimeZone });
          return requestResponse;
        };

        const allCalendars = await Promise.all(calendarsToFecth.map(fetchAllCalendars));
        const response = allCalendars.reduce(
          (acc, calendar) => {
            return {
              availableList: [...acc.availableList, ...calendar.availableList],
              availableListAddress: [...(acc.availableListAddress ?? []), ...(calendar.availableListAddress ?? [])]
            };
          },
          { availableList: [], availableListAddress: [] }
        );
        return { data: response };
      }
    })
  })
});

export const { useGetAvailableTimesMutation } = availableTimes;
